// src/lib/print.ts — TIPĂRIREA HĂRȚII (REQ-FMT-005, 5 contracte: „hărți care să poată fi tipărite
// sau salvate ca documente"). Helperi PURI, fără DOM: pagina de tipărire e un document HTML complet
// (A4 peisaj) construit ca STRING și scris într-un tab nou de client (map.client → print_current_view);
// browserul tipărește sau salvează ca PDF; butonul „Imagine PNG" descarcă aceeași captură pentru
// prezentări/documente (cerința: „tipărite SAU salvate ca documente și prezentări"). Nimic pe server.
//
// Conținut: antet (titlu, hartă, primărie + stemă, data) · imaginea canvas-ului MapLibre (vederea
// curentă, la rezoluția ecranului × devicePixelRatio) · legenda straturilor VIZIBILE cu ACELEAȘI
// simboluri ca legenda de pe hartă (swatch_svg oglindește LayerSwatch/RoleSwatch din map.client —
// modificările se fac în AMBELE locuri; + rânduri pentru stratul virtual „Acte" — emise/anulate,
// culoarea e starea — și pentru desen/măsurători, care sunt ÎN captură) · bară de scară + nord · atribuirea fundalului (obligatorie
// OSM/Esri) · mențiunea „nu ține loc de certificat de urbanism". Legenda stă în dreapta hărții;
// peste 12 straturi vizibile trece SUB hartă, pe 4 coloane (altfel pagina A4 se rupea în două).

export interface PrintLegendItem {
	name: string
	svg: string // simbolul, deja HTML (din swatch_svg)
	// Categoria unui strat cu stil pe valoare: rând INDENTAT sub stratul ei, cu eticheta categoriei
	// singură — oglinda legendei de pe ecran (`pl-4`). Până pe 03.11 categoriile ieșeau la coada listei,
	// ca „<strat> — <categorie>”, iar eticheta se tăia tocmai pe partea care conta („Zona … — 1…”).
	sub?: boolean
}
export interface PrintScale {
	label: string // ex. „200 m"
	px: number // lățimea barei în pixeli CSS
}
export interface PrintModel {
	title: string // titlul dat de utilizator ("" = fără)
	map_name: string
	org_name: string // "" = fără primărie
	stema_url: string // "" = fără stemă
	date_label: string
	image_data_url: string // PNG base64 al canvas-ului
	image_aspect: number // lățime / înălțime (păstrează proporția în pagină)
	image_width_px: number // lățimea canvas-ului în px CSS — bara de scară se exprimă ca % din ea
	legend: PrintLegendItem[]
	scale: PrintScale
	bearing_deg: number // rotația hărții (0 = nordul sus)
	attribution: string // sursa fundalului
	mode_3d: boolean
	// Straturile desenate PARȚIAL (serverul a semnalat `truncated`). Gol = nimic de semnalat.
	// ⚠️ Fără asta, o hartă trunchiată (strat mare privit istoric, unde tile-urile sunt dezactivate)
	// ieșea pe hârtie ca un plan COMPLET — exact tiparul „pare tot, dar nu e".
	truncated_layers?: string[]
}

/** Eticheta barei (distanțe „frumoase" → fără zecimale: „500 m", „5 km", „20 km"). */
function scale_label(meters: number): string {
	return meters < 1000 ? `${meters} m` : `${meters / 1000} km`
}

/** Numere „frumoase" pentru bara de scară: 1, 2, 5 × 10^n metri. */
const NICE = [1, 2, 5]

/**
 * Bara de scară: cea mai mare distanță „frumoasă" care încape în `max_px` la `meters_per_px`.
 * Întoarce lățimea EXACTĂ în pixeli (bara e desenată la dimensiunea distanței, nu invers).
 */
export function nice_scale(meters_per_px: number, max_px = 160): PrintScale {
	if (!(meters_per_px > 0) || !(max_px > 0)) {
		return { label: "", px: 0 }
	}
	const max_m = meters_per_px * max_px
	let best = 1
	for (let e = 0; e < 8; e++) {
		for (const n of NICE) {
			const m = n * 10 ** e
			if (m <= max_m) {
				best = m
			}
		}
	}
	return { label: scale_label(best), px: Math.round(best / meters_per_px) }
}

/** Familia de simbol după tipul geometriei (ca LayerSwatch). */
export function legend_kind(geometry_type: string): "polygon" | "line" | "point" {
	const gt = (geometry_type ?? "").toLowerCase()
	if (gt.includes("polygon")) {
		return "polygon"
	}
	if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		return "line"
	}
	return "point"
}

/** Escapare HTML pentru textele venite din date (nume de straturi/hărți, titlul utilizatorului). */
export function escape_html(s: string): string {
	return s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
}

/** Culoare CSS sigură (hex/rgb/rgba/hsl/nume simplu) — altfel gri; anti-injecție în atributul SVG. */
function safe_color(c: string): string {
	return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]{3,20})$/i.test(c.trim())
		? c.trim()
		: "#64748b"
}

/**
 * Simbolul de legendă ca SVG string — OGLINDA lui LayerSwatch/RoleSwatch (map.client): careu
 * (poligon), linie frântă (linie), disc (punct); UTR = careu împărțit verde, derogare = triunghi
 * de atenționare amber (REQ-URB-001).
 */
/**
 * Liniile hașurii din careul de legendă (B, 13.10) — OGLINDA lui `hatch_lines` din LayerSwatch (map.client):
 * trei linii pe direcția cuantizată (0/45/90/135), amândouă direcțiile dacă e încrucișată. Fără
 * dependențe: cuantizarea e repetată aici ca modulul să rămână pur.
 */
export function hatch_paths(angle: number, crossed: boolean): string[] {
	const a = ((Math.round(angle) % 180) + 180) % 180
	const q0 = Math.round(a / 45) * 45
	const q = q0 >= 180 ? 0 : q0
	const dirs = crossed ? [q, (q + 90) % 180] : [q]
	const of = (d: number): string[] =>
		d === 45
			? ["M1 17 L17 1", "M1 9 L9 1", "M9 17 L17 9"]
			: d === 135
				? ["M1 1 L17 17", "M1 9 L9 17", "M9 1 L17 9"]
				: d === 90
					? ["M5 1 V17", "M9 1 V17", "M13 1 V17"]
					: ["M1 5 H17", "M1 9 H17", "M1 13 H17"]
	return dirs.flatMap(of)
}

export function swatch_svg(
	kind: "polygon" | "line" | "point" | "utr" | "derogare",
	color = "#64748b",
	stroke = color,
	fill_opacity = 0.7, // 0 = fără umplere (poligon doar-contur) / fără simbol (punct doar-etichetă)
	hatch: { color: string; angle: number; crossed: boolean } | null = null, // hașura peste careu (B)
): string {
	const c = safe_color(color)
	const s = safe_color(stroke)
	const hollow = fill_opacity <= 0
	const open = `<svg width="18" height="18" viewBox="0 0 18 18" style="flex-shrink:0">`
	switch (kind) {
		case "utr":
			return (
				`${open}<rect x="1" y="1" width="16" height="16" rx="2" fill="#059669" fill-opacity="0.15" stroke="#059669" stroke-width="1.5"/>` +
				`<path d="M9 1 V17 M1 9 H17" stroke="#059669" stroke-width="1.2"/></svg>`
			)
		case "derogare":
			return (
				`${open}<path d="M9 2 L16.5 15.5 H1.5 Z" fill="#d97706" fill-opacity="0.2" stroke="#d97706" stroke-width="1.5" stroke-linejoin="round"/>` +
				`<path d="M9 7 V11" stroke="#d97706" stroke-width="1.8" stroke-linecap="round"/><circle cx="9" cy="13.4" r="1" fill="#d97706"/></svg>`
			)
		case "polygon": {
			const lines = hatch
				? hatch_paths(hatch.angle, hatch.crossed)
						.map(
							(d) =>
								`<path d="${d}" stroke="${safe_color(hatch.color)}" stroke-width="1.2" fill="none"/>`,
						)
						.join("")
				: ""
			return `${open}<rect x="1" y="1" width="16" height="16" rx="2" fill="${hollow ? "none" : c}" fill-opacity="0.7" stroke="${s}" stroke-width="1.5"/>${lines}</svg>`
		}
		case "line":
			return `${open}<path d="M2 14 L6 6 L12 10 L16 4" fill="none" stroke="${c}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
		default:
			return hollow
				? `${open}<text x="9" y="13" text-anchor="middle" font-size="11" font-family="sans-serif" fill="${c}">Aa</text></svg>`
				: `${open}<circle cx="9" cy="9" r="6" fill="${c}" fill-opacity="0.85" stroke="${s}" stroke-width="1.5"/></svg>`
	}
}

/**
 * Simbolul de legendă pentru un strat cu ICONIȚĂ (decizia 1.4): `<img>` 18 px — OGLINDA ramurii cu
 * iconiță din LayerSwatch (map.client). Acceptă DOAR data URL de imagine (documentul tipărit e
 * self-contained: clientul aduce iconița ca data URL înainte) sau o cale/URL http(s) (fallback);
 * altceva → discul generic (anti-injecție în atribut).
 */
export function swatch_img(src: string, color = "#64748b"): string {
	const ok =
		/^(data:image\/(png|svg\+xml|jpeg|webp);base64,[A-Za-z0-9+/=]+|https?:\/\/[^"'<>\s]+|\/[^"'<>\s]*)$/.test(
			src,
		)
	if (!ok) {
		return swatch_svg("point", color)
	}
	return `<img src="${escape_html(src)}" width="18" height="18" style="flex-shrink:0;object-fit:contain" alt="">`
}

/**
 * Culoare DERIVATĂ dintr-un hex (#rgb/#rrggbb): `factor` < 1 întunecă, > 1 luminează (spre alb).
 * Pentru rândurile „strat derivat" (filtre salvate, decizia 2.2): aceeași familie cromatică ca
 * stratul părinte, distinctă în legendă. Culorile nerecunoscute se întorc neschimbate.
 */
export function shade_color(hex: string, factor: number): string {
	const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
	if (!m || !Number.isFinite(factor) || factor < 0) {
		return hex
	}
	let h = m[1]
	if (h.length === 3) {
		h = h
			.split("")
			.map((c) => c + c)
			.join("")
	}
	const ch = (i: number) => parseInt(h.slice(i, i + 2), 16)
	const mix = (v: number) => {
		const out = factor <= 1 ? v * factor : v + (255 - v) * Math.min(1, factor - 1)
		return Math.max(0, Math.min(255, Math.round(out)))
	}
	const to2 = (n: number) => n.toString(16).padStart(2, "0")
	return `#${to2(mix(ch(0)))}${to2(mix(ch(2)))}${to2(mix(ch(4)))}`
}

const PRINT_CSS = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #fff; color: #1f2937; font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; }
  body { padding: 10mm; }
  .bar { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
  .bar input { flex: 1; font: inherit; font-size: 14px; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; }
  .bar button { font: inherit; font-size: 14px; padding: 6px 14px; border-radius: 6px; border: 1px solid #0e7490; background: #0e7490; color: #fff; cursor: pointer; }
  .bar .alt { font: inherit; font-size: 14px; padding: 6px 12px; border-radius: 6px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; text-decoration: none; white-space: nowrap; }
  .bar .hint { font-size: 12px; color: #64748b; }
  .page { width: 277mm; min-height: 190mm; margin: 0 auto; display: flex; flex-direction: column; gap: 4mm; }
  header { display: flex; align-items: center; justify-content: space-between; gap: 8mm; border-bottom: 2px solid #0e7490; padding-bottom: 2mm; }
  header .who { display: flex; align-items: center; gap: 4mm; }
  header img.stema { height: 14mm; width: auto; }
  header h1 { font-size: 16pt; color: #0f172a; line-height: 1.2; }
  header .sub { font-size: 10pt; color: #475569; margin-top: 1mm; }
  header .meta { font-size: 9pt; color: #64748b; text-align: right; white-space: nowrap; }
  .body { display: grid; grid-template-columns: 1fr 58mm; gap: 4mm; align-items: start; }
  .body.wide { grid-template-columns: minmax(0, 1fr); }
  .map { position: relative; border: 1px solid #cbd5e1; border-radius: 3px; overflow: hidden; background: #f1f5f9; margin: 0 auto;
         aspect-ratio: var(--ar); width: min(100%, calc(var(--mh) * var(--ar))); }
  .map img { display: block; width: 100%; height: 100%; object-fit: contain; }
  .north { position: absolute; top: 3mm; right: 3mm; width: 12mm; height: 12mm; background: rgba(255,255,255,.85); border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid #cbd5e1; }
  .north svg { width: 9mm; height: 9mm; }
  .scale { position: absolute; left: 3mm; bottom: 3mm; background: rgba(255,255,255,.85); padding: 1mm 2mm; border: 1px solid #cbd5e1; border-radius: 2px; font-size: 8pt; color: #0f172a; white-space: nowrap; box-sizing: content-box; }
  .scale .rule { width: 100%; height: 2mm; border: 1.5px solid #0f172a; border-top: none; margin-bottom: 0.5mm; }
  aside { border: 1px solid #cbd5e1; border-radius: 3px; padding: 3mm; font-size: 9pt; min-width: 0; }
  aside h2 { font-size: 8pt; text-transform: uppercase; letter-spacing: .06em; color: #64748b; margin-bottom: 2mm; }
  aside ul { list-style: none; display: flex; flex-direction: column; gap: 1.2mm; }
  .wide aside ul { display: block; column-count: 4; column-gap: 4mm; font-size: 8pt; }
  .wide aside li { break-inside: avoid; margin-bottom: 1mm; }
  aside li { display: flex; align-items: center; gap: 2mm; min-width: 0; }
  aside li svg, aside li img { flex-shrink: 0; }
  aside li.sub { padding-left: 4mm; font-size: .92em; color: #334155; }
  aside li span { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.2; }
  .wide aside li span { display: block; min-width: 0; text-overflow: ellipsis; white-space: nowrap; }
  aside .empty { color: #94a3b8; font-style: italic; }
  footer { display: flex; justify-content: space-between; gap: 6mm; font-size: 8pt; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 1.5mm; }
  footer .note { color: #92400e; }
  @media print { .bar { display: none; } body { padding: 0; } .page { width: auto; max-height: 190mm; overflow: hidden; } }
`

/** Documentul HTML complet al paginii de tipărire (self-contained: CSS inline, imagine base64). */
export function build_print_html(m: PrintModel): string {
	const title = escape_html(m.title.trim())
	const map_name = escape_html(m.map_name)
	const org = escape_html(m.org_name)
	const legend =
		m.legend.length === 0
			? `<div class="empty">Niciun strat vizibil.</div>`
			: `<ul>${m.legend.map((l) => `<li${l.sub ? ' class="sub"' : ""}>${l.svg}<span title="${escape_html(l.name)}">${escape_html(l.name)}</span></li>`).join("")}</ul>`
	const stema =
		m.stema_url && /^(https?:\/\/|\/)/.test(m.stema_url)
			? `<img class="stema" src="${escape_html(m.stema_url)}" alt="">`
			: ""
	const rot = Number.isFinite(m.bearing_deg) ? -m.bearing_deg : 0
	// Bara de scară ca PROCENT din lățimea imaginii: imaginea e redimensionată în pagină, deci o
	// lățime fixă în px ar fi greșită cu raportul canvas/pagină (×2 pe un desktop 1600px).
	const pct = m.image_width_px > 0 ? (m.scale.px / m.image_width_px) * 100 : 0
	const scale =
		m.scale.px > 0 && pct > 0 && pct <= 100
			? `<div class="scale" style="width:${pct.toFixed(3)}%"><div class="rule"></div>${escape_html(m.scale.label)}</div>`
			: ""
	// Layout: legenda în dreapta (≤12 straturi) sau SUB hartă pe 4 coloane; înălțimea hărții scade cu
	// rândurile legendei (~6 mm/rând), altfel pagina A4 (190 mm utili) se rupea în două.
	const wide = m.legend.length > 12
	const rows = wide ? Math.ceil(m.legend.length / 4) : 0
	const map_h = wide ? Math.max(70, 135 - rows * 6) : 150
	const ar = m.image_aspect > 0 ? m.image_aspect.toFixed(4) : "1.5"
	const doc_title = title || map_name
	return `<!doctype html>
<html lang="ro"><head><meta charset="utf-8"><title>${doc_title}</title><style>${PRINT_CSS}</style></head>
<body>
<div class="bar">
  <input id="titlu" type="text" placeholder="Titlu (opțional) — apare în antet" value="${title}" aria-label="Titlu">
  <button type="button" onclick="window.print()">🖨 Tipărește / salvează PDF</button>
  <a id="png" class="alt" download="harta.png" href="#">⤓ Imagine PNG</a>
  <span class="hint">Din dialogul de tipărire alegeți imprimanta sau „Salvează ca PDF”; imaginea PNG intră direct într-o prezentare sau un document.</span>
</div>
<div class="page">
  <header>
    <div class="who">${stema}<div><h1 id="h-titlu">${doc_title}</h1><div class="sub">${title ? `${map_name}${org ? " · " : ""}` : ""}${org}${m.mode_3d ? " · vedere 3D" : ""}</div></div></div>
    <div class="meta">${escape_html(m.date_label)}<br>Urbanism by Vego</div>
  </header>
  <div class="body${wide ? " wide" : ""}">
    <div class="map" style="--ar:${ar};--mh:${map_h}mm">
      <img src="${m.image_data_url}" alt="Harta">
      <div class="north" title="Nord"><svg viewBox="0 0 24 24" style="transform:rotate(${rot}deg)"><path d="M12 2 L17 20 L12 16 L7 20 Z" fill="#0f172a"/><text x="12" y="11" font-size="7" text-anchor="middle" fill="#fff" font-family="sans-serif" font-weight="700">N</text></svg></div>
      ${scale}
    </div>
    <aside><h2>Legendă</h2>${legend}</aside>
  </div>
  <footer>
    <span>${escape_html(m.attribution)}</span>
    <span class="note">Document informativ generat din platformă — nu ține loc de certificat de urbanism.${
			m.truncated_layers && m.truncated_layers.length > 0
				? ` <strong>ATENȚIE: afișare parțială — ${escape_html(m.truncated_layers.join(", "))} ${m.truncated_layers.length === 1 ? "nu este afișat integral" : "nu sunt afișate integral"}.</strong>`
				: ""
		}</span>
  </footer>
</div>
<script>
  (function(){var i=document.getElementById('titlu'),h=document.getElementById('h-titlu'),base=${JSON.stringify(m.map_name).replace(/</g, "\\u003c")};
   i.addEventListener('input',function(){h.textContent=i.value.trim()||base;document.title=h.textContent;});
   var a=document.getElementById('png'),img=document.querySelector('.map img');if(a&&img){a.href=img.getAttribute('src');
   a.addEventListener('click',function(){var t=(i.value.trim()||base).replace(/[^\\w\\u00C0-\\u024F -]+/g,'').trim()||'harta';a.download=t+'.png';});}})();
</script>
</body></html>`
}
